import { Button, Spin, Typography } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ComponentType } from "react";

/**
 * Host tải-lười cho viewer 3D của SCR-037 (S11.6, layout v2).
 *
 * Module này CHỈ sở hữu ranh giới tải-lười và các fallback trung thực: panel MỞ SẴN khi vào
 * trang (loader gọi ngay từ mount), trạng thái đang tải, nhánh chưa có model, nhánh lỗi + thử
 * lại. Engine, orbit, preset, manifest và asset của viewer thuộc S11.8–S11.9 — S11.6 KHÔNG
 * import `three`, `@react-three/fiber`, file `.glb` hay module S11.8 nào ở cấp module: viewer
 * chỉ vào DOM qua loader động do composition boundary của S11.8 truyền vào, nhờ đó code 3D
 * không nằm trong bundle khởi tạo của khu quản trị.
 */

/** Props nghiệp vụ mà viewer 3D nhận từ host — S11.8 sở hữu cách diễn giải. */
export type EmbeddedSceneProps = Readonly<{
  cemeterySlug?: string;
  publicGraveKey?: string;
  positionCode?: string;
  graveType?: string;
}>;

/** Mô-đun viewer đã tải xong: default export là component nhận {@link EmbeddedSceneProps}. */
export type EmbeddedSceneModule = Readonly<{
  default: ComponentType<EmbeddedSceneProps>;
}>;

/** Hàm tải động do composition boundary cung cấp (`() => import("./EmbeddedGraveScene")` ở S11.8). */
export type EmbeddedSceneLoader = () => Promise<EmbeddedSceneModule>;

/** Vòng đời của panel: đang tải → có viewer / không có model / lỗi. */
type ScenePhase = "loading" | "ready" | "error" | "unavailable";

type EmbeddedGraveSceneSlotProps = Readonly<{
  /** Không truyền loader thì panel nói thật là chưa có model — không dựng cảnh giả. */
  sceneLoader?: EmbeddedSceneLoader;
  cemeterySlug?: string;
  publicGraveKey?: string;
  positionCode?: string;
  graveType?: string;
}>;

/**
 * Panel vị trí 3D của hồ sơ mộ: mở sẵn khi vào trang, không có affordance ẩn/hiện. Mọi nhánh
 * thất bại đều quay về fallback văn bản/2D kèm đường thử lại — không bao giờ có canvas rỗng hay
 * cảnh mô phỏng.
 */
export function EmbeddedGraveSceneSlot({
  sceneLoader,
  cemeterySlug,
  publicGraveKey,
  positionCode,
  graveType,
}: EmbeddedGraveSceneSlotProps) {
  const [phase, setPhase] = useState<ScenePhase>("loading");
  const [Scene, setScene] = useState<ComponentType<EmbeddedSceneProps> | null>(null);
  // Mỗi lần thử lại là một lượt tải; kết quả của lượt cũ bị bỏ qua.
  const runRef = useRef(0);

  const open = useCallback(() => {
    if (sceneLoader === undefined) {
      setPhase("unavailable");
      return;
    }
    const run = runRef.current + 1;
    runRef.current = run;
    setPhase("loading");
    sceneLoader().then(
      (module) => {
        if (runRef.current !== run) return;
        // Bọc trong updater: default export là component (function), không được để React
        // hiểu nhầm thành hàm cập nhật state.
        setScene(() => module.default);
        setPhase("ready");
      },
      () => {
        if (runRef.current !== run) return;
        setPhase("error");
      },
    );
  }, [sceneLoader]);

  // Không còn trạng thái `idle`: panel mở sẵn nên lượt tải đầu tiên bắt đầu ngay khi mount.
  // `open` chỉ đổi identity khi loader đổi (loader ổn định ở trang chi tiết) nên effect chạy
  // đúng một lượt; `runRef` vẫn bỏ qua kết quả của lượt cũ nếu điều đó thay đổi.
  useEffect(() => {
    open();
  }, [open]);

  const sceneProps: EmbeddedSceneProps = {
    ...(cemeterySlug !== undefined ? { cemeterySlug } : {}),
    ...(publicGraveKey !== undefined ? { publicGraveKey } : {}),
    ...(positionCode !== undefined ? { positionCode } : {}),
    ...(graveType !== undefined ? { graveType } : {}),
  };

  return (
    // Tên khối chỉ còn ở `aria-label`: khi 3D đã là avatar nằm trong thẻ hồ sơ, một nhãn chữ
    // "Vị trí 3D" không thêm thông tin gì cho người nhìn, nhưng vẫn cần để định danh landmark.
    <section className="s11-grave-panel s11-grave-3d" aria-label="Vị trí 3D">
      {phase === "loading" ? (
        <div role="status" aria-label="Đang tải mô hình 3D">
          <Spin />
        </div>
      ) : null}

      {phase === "unavailable" ? (
        <Typography.Paragraph className="s11-grave-3d-message">
          Chưa có mô hình 3D cho vị trí này
        </Typography.Paragraph>
      ) : null}

      {phase === "error" ? (
        <div className="s11-grave-3d-fallback">
          <Typography.Paragraph className="s11-grave-3d-message">
            Không thể tải mô hình 3D
          </Typography.Paragraph>
          <Button onClick={open}>Thử lại</Button>
        </div>
      ) : null}

      {phase === "ready" && Scene !== null ? <Scene {...sceneProps} /> : null}
    </section>
  );
}
